import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Database, 
  FileText, 
  Search, 
  Plus, 
  Trash2, 
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Tag,
  MessageSquare,
  Settings,
  Play
} from 'lucide-react';
import { api } from '../../services/api';

// KB V1 Admin Component - Unified Knowledge Hub
// Includes: Sources, Entries, Query, Tone Config, AI Test

const TONE_PRESETS = [
  { value: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
  { value: 'professional', label: 'Professional', description: 'Formal and polished' },
  { value: 'direct', label: 'Direct', description: 'Straight to the point' },
  { value: 'luxury', label: 'Luxury', description: 'Sophisticated and exclusive' },
  { value: 'cheeky', label: 'Cheeky', description: 'Playful and bold' },
  { value: 'custom', label: 'Custom', description: 'Define your own' },
];

const KBV1Admin = () => {
  const [activeTab, setActiveTab] = useState('sources');
  const [sources, setSources] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);
  const [querySubmitted, setQuerySubmitted] = useState(false);
  
  // Form states
  const [showAddSource, setShowAddSource] = useState(false);
  const [newSource, setNewSource] = useState({ name: '', type: 'csv', sync_schedule: 'manual' });
  
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({ title: '', content: '', category: 'inventory', tags: [] });
  const [tagInput, setTagInput] = useState('');
  
  // Query state
  const [queryText, setQueryText] = useState('');
  const [queryResults, setQueryResults] = useState([]);
  const [queryLoading, setQueryLoading] = useState(false);
  
  // Tone settings state
  const [tonePreset, setTonePreset] = useState('friendly');
  const [toneCustom, setToneCustom] = useState('');
  const [conversionAssistEnabled, setConversionAssistEnabled] = useState(true);
  const [conversionAssistStrength, setConversionAssistStrength] = useState(2);
  const [toneSaving, setToneSaving] = useState(false);
  
  // AI Test state
  const [testPrompt, setTestPrompt] = useState('What are your opening hours?');
  const [testOutput, setTestOutput] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  
  // Uploads state
  const [uploads, setUploads] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  // Safe toast with cleanup
  const showToastMsg = useCallback((type, message) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ type, message });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [me, sourcesData, entriesData, uploadsData] = await Promise.all([
        api.getMe(),
        api.getKBSources(),
        api.getKBEntries(),
        api.getKBUploads()
      ]);
      // Handle both direct arrays and wrapped responses
      setSources(Array.isArray(sourcesData) ? sourcesData : sourcesData?.data || []);
      setEntries(Array.isArray(entriesData) ? entriesData : entriesData?.data || []);
      setUploads(Array.isArray(uploadsData) ? uploadsData : uploadsData?.data || []);

      // Tone settings (optional)
      const dealerId = me?.dealer_id;
      if (dealerId) {
        const tone = await api.getDealerTone(dealerId);
        setTonePreset(tone?.tone_preset || 'friendly');
        setToneCustom(tone?.tone_custom || '');
        setConversionAssistEnabled(tone?.conversion_assist_enabled !== false);
        setConversionAssistStrength(Number.isFinite(tone?.conversion_assist_strength) ? Number(tone.conversion_assist_strength) : 2);
      }
    } catch (error) {
      console.error('Failed to load KB data:', error);
      showToastMsg('error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [showToastMsg]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleCreateSource = async (e) => {
    e.preventDefault();
    if (!newSource.name.trim()) return;
    
    try {
      await api.createKBSource(newSource);
      showToastMsg('success', 'Source created');
      setShowAddSource(false);
      setNewSource({ name: '', type: 'csv', sync_schedule: 'manual' });
      loadData();
    } catch (error) {
      showToastMsg('error', error.message || 'Failed to create source');
    }
  };
  
  const handleFileUpload = async (file) => {
    const allowedTypes = [
      'text/markdown',
      'text/plain',
      'application/pdf',
      'text/csv',
      'application/json'
    ];
    const allowedExtensions = ['.md', '.txt', '.pdf', '.csv', '.json'];
    
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExt);
    
    if (!isValidType) {
      showToastMsg('error', 'Invalid file type. Allowed: MD, TXT, PDF, CSV, JSON');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      showToastMsg('error', 'File too large. Maximum size: 10MB');
      return;
    }
    
    setUploadingFile(true);
    try {
      await api.uploadKBDocument(file);
      showToastMsg('success', `Uploaded ${file.name}`);
      loadData();
    } catch (error) {
      showToastMsg('error', error.message || 'Upload failed');
    } finally {
      setUploadingFile(false);
    }
  };
  
  const handleDeleteUpload = async (id) => {
    if (!window.confirm('Delete this uploaded file? This will also remove it from the knowledge base.')) return;
    try {
      await api.deleteKBUpload(id);
      showToastMsg('success', 'File deleted');
      loadData();
    } catch (error) {
      showToastMsg('error', error.message || 'Failed to delete file');
    }
  };
  
  const handleDeleteSource = async (id) => {
    if (!window.confirm('Delete this source? This will also remove all associated entries.')) return;
    try {
      await api.deleteKBSource(id);
      showToastMsg('success', 'Source deleted');
      loadData();
    } catch (error) {
      showToastMsg('error', error.message || 'Failed to delete source');
    }
  };
  
  const handleCreateEntry = async (e) => {
    e.preventDefault();
    if (!newEntry.title.trim() || !newEntry.content.trim()) return;
    
    try {
      await api.createKBEntry({ ...newEntry, metadata: {} });
      showToastMsg('success', 'Entry created');
      setShowAddEntry(false);
      setNewEntry({ title: '', content: '', category: 'inventory', tags: [] });
      loadData();
    } catch (error) {
      showToastMsg('error', error.message || 'Failed to create entry');
    }
  };
  
  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.deleteKBEntry(id);
      showToastMsg('success', 'Entry deleted');
      loadData();
    } catch (error) {
      showToastMsg('error', 'Failed to delete');
    }
  };
  
  const handleQuery = async (e) => {
    e.preventDefault();
    if (!queryText.trim()) return;
    
    setQueryLoading(true);
    setQuerySubmitted(true);
    setQueryResults([]);
    
    try {
      const data = await api.queryKB(queryText, 5, 0.7);
      const results = data?.results || data?.data?.results || [];
      setQueryResults(results);
    } catch (error) {
      showToastMsg('error', 'Query failed');
      setQueryResults([]);
    } finally {
      setQueryLoading(false);
    }
  };
  
  const handleSaveTone = async () => {
    setToneSaving(true);
    try {
      const me = await api.getMe();
      const dealerId = me?.dealer_id;
      if (!dealerId) throw new Error('No dealer ID on user');

      await api.updateDealerTone(dealerId, {
        tone_preset: tonePreset,
        tone_custom: tonePreset === 'custom' ? toneCustom : null,
        writing_style: null,
        conversion_assist_enabled: !!conversionAssistEnabled,
        conversion_assist_strength: Number(conversionAssistStrength),
      });
      showToastMsg('success', 'Tone settings saved');
    } catch (error) {
      showToastMsg('error', error.message || 'Failed to save tone settings');
    } finally {
      setToneSaving(false);
    }
  };
  
  const runAiTest = async () => {
    if (!testPrompt.trim() || testRunning) return;
    setTestRunning(true);
    setTestOutput('');
    
    try {
      const token = api.token || localStorage.getItem('token');
      const res = await fetch(`/api/ai/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: testPrompt }],
          profile: 'inbound_chat_qual',
          channel: 'crm-admin-test',
        }),
      });

      if (!res.ok || !res.body) {
        const txt = await res.text().catch(() => '');
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        if (chunk) setTestOutput(prev => prev + chunk);
      }
    } catch (e) {
      console.error(e);
      showToastMsg('error', e.message || 'AI test failed');
    } finally {
      setTestRunning(false);
    }
  };
  
  const addTag = () => {
    if (tagInput.trim() && !newEntry.tags.includes(tagInput.trim())) {
      setNewEntry({ ...newEntry, tags: [...newEntry.tags, tagInput.trim()] });
      setTagInput('');
    }
  };
  
  const removeTag = (tag) => {
    setNewEntry({ ...newEntry, tags: newEntry.tags.filter(t => t !== tag) });
  };

  const categoryColors = {
    inventory: 'var(--brand-primary, #3b82f6)',
    pricing: '#10b981', 
    policy: '#f59e0b',
    procedure: '#8b5cf6',
    custom: '#64748b'
  };

  const truncateContent = (content, maxLength = 150) => {
    if (!content || typeof content !== 'string') return '';
    return content.length > maxLength ? content.substring(0, maxLength) + '...' : content;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const tabs = [
    { id: 'sources', label: 'Sources', icon: Database },
    { id: 'entries', label: 'Entries', icon: FileText },
    { id: 'uploads', label: 'Uploads', icon: FileText },
    { id: 'query', label: 'Query', icon: Search },
    { id: 'tone', label: 'Tone & Style', icon: Settings },
    { id: 'test', label: 'AI Test', icon: MessageSquare },
  ];

  return (
    <div style={{ maxWidth: '1000px', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ color: '#f1f5f9', fontSize: '24px', fontWeight: 600, margin: '0 0 8px 0', display: 'flex', alignItems: 'center' }}>
          <Database size={24} style={{ marginRight: '8px' }} />
          Knowledge Base
        </h1>
        <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
          Manage inventory data, AI tone, and test responses
        </p>
      </div>
      
      {/* Toast */}
      {toast && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: toast.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
          color: toast.type === 'success' ? '#86efac' : '#fca5a5',
        }}>
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {toast.message}
        </div>
      )}
      
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #334155', paddingBottom: '16px', flexWrap: 'wrap' }}>
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            style={{
              padding: '10px 16px',
              background: activeTab === id ? 'var(--brand-primary, #3b82f6)' : 'transparent',
              border: '1px solid #334155',
              borderRadius: '8px',
              color: activeTab === id ? '#fff' : '#94a3b8',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Icon size={16} style={{ marginRight: '6px' }} />
            {label}
            {id === 'sources' && sources.length > 0 && (
              <span style={{ marginLeft: '8px', padding: '2px 8px', background: 'rgba(255,255,255,0.2)', borderRadius: '12px', fontSize: '12px' }}>
                {sources.length}
              </span>
            )}
            {id === 'entries' && entries.length > 0 && (
              <span style={{ marginLeft: '8px', padding: '2px 8px', background: 'rgba(255,255,255,0.2)', borderRadius: '12px', fontSize: '12px' }}>
                {entries.length}
              </span>
            )}
            {id === 'uploads' && uploads.length > 0 && (
              <span style={{ marginLeft: '8px', padding: '2px 8px', background: 'rgba(255,255,255,0.2)', borderRadius: '12px', fontSize: '12px' }}>
                {uploads.length}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Sources Tab */}
      {activeTab === 'sources' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: 600, margin: 0 }}>Knowledge Sources</h2>
            <button
              onClick={() => setShowAddSource(true)}
              style={{ padding: '8px 16px', background: 'var(--brand-primary, #3b82f6)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <Plus size={16} style={{ marginRight: '6px' }} />
              Add Source
            </button>
          </div>
          
          {showAddSource && (
            <form onSubmit={handleCreateSource} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px', display: 'block' }}>Source Name</label>
                  <input
                    type="text"
                    value={newSource.name}
                    onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                    placeholder="e.g., CDK Inventory Feed"
                    style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px', display: 'block' }}>Type</label>
                  <select
                    value={newSource.type}
                    onChange={(e) => setNewSource({ ...newSource, type: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9', fontSize: '14px' }}
                  >
                    <option value="csv">CSV Import (CDK)</option>
                    <option value="manual">Manual Entries</option>
                    <option value="webhook">Webhook/API</option>
                    <option value="sftp">SFTP Pull</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAddSource(false)} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #334155', borderRadius: '8px', color: '#94a3b8', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" style={{ padding: '8px 16px', background: 'var(--brand-primary, #3b82f6)', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer' }}>
                  Create Source
                </button>
              </div>
            </form>
          )}
          
          {loading ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: '32px' }}>Loading...</div>
          ) : sources.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <Database size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
              <div style={{ color: '#94a3b8', fontSize: '16px' }}>No sources yet</div>
              <div style={{ color: '#64748b', fontSize: '13px' }}>Add a source to start importing inventory data</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sources.map(source => (
                <div key={source.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <div style={{ color: '#f1f5f9', fontWeight: 500 }}>{source.name}</div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                        <span style={{ padding: '2px 8px', background: source.type === 'csv' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(139, 92, 246, 0.2)', color: source.type === 'csv' ? '#93c5fd' : '#c4b5fd', borderRadius: '4px', fontSize: '12px' }}>
                          {source.type === 'csv' ? '📊 CSV' : source.type === 'webhook' ? '🔗 Webhook' : source.type === 'sftp' ? '📡 SFTP' : '✏️ Manual'}
                        </span>
                        <span style={{ padding: '2px 8px', background: 'rgba(100, 116, 139, 0.2)', color: '#94a3b8', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                          <RefreshCw size={10} style={{ marginRight: '4px' }} />
                          {source.sync_schedule === 'daily' ? 'Daily' : 'Manual'}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', color: source.enabled ? '#86efac' : '#fca5a5', fontSize: '13px' }}>
                        {source.enabled ? <><CheckCircle size={14} style={{ marginRight: '4px' }} /> Enabled</> : <><XCircle size={14} style={{ marginRight: '4px' }} /> Disabled</>}
                      </span>
                      <button 
                        onClick={() => handleDeleteSource(source.id)}
                        style={{ padding: '4px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  {source.last_sync_at && (
                    <div style={{ color: '#64748b', fontSize: '12px' }}>
                      Last synced: {formatDate(source.last_sync_at)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Entries Tab */}
      {activeTab === 'entries' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: 600, margin: 0 }}>Knowledge Entries</h2>
            <button
              onClick={() => setShowAddEntry(true)}
              style={{ padding: '8px 16px', background: 'var(--brand-primary, #3b82f6)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <Plus size={16} style={{ marginRight: '6px' }} />
              Add Entry
            </button>
          </div>
          
          {showAddEntry && (
            <form onSubmit={handleCreateEntry} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px', display: 'block' }}>Title</label>
                <input
                  type="text"
                  value={newEntry.title}
                  onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
                  placeholder="e.g., 2024 Toyota Camry Hybrid"
                  style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9', fontSize: '14px' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px', display: 'block' }}>Category</label>
                  <select
                    value={newEntry.category}
                    onChange={(e) => setNewEntry({ ...newEntry, category: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9', fontSize: '14px' }}
                  >
                    <option value="inventory">Inventory</option>
                    <option value="pricing">Pricing</option>
                    <option value="policy">Policy</option>
                    <option value="procedure">Procedure</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px', display: 'block' }}>Tags</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '6px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', minHeight: '40px' }}>
                    {newEntry.tags.map(tag => (
                      <span key={tag} style={{ padding: '2px 8px', background: 'var(--brand-primary, #3b82f6)', color: '#fff', borderRadius: '4px', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                        {tag}
                        <button 
                          type="button" 
                          onClick={() => removeTag(tag)} 
                          style={{ marginLeft: '4px', background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder="Add tag..."
                      style={{ flex: 1, minWidth: '80px', background: 'transparent', border: 'none', color: '#f1f5f9', fontSize: '14px', outline: 'none' }}
                    />
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px', display: 'block' }}>Content</label>
                <textarea
                  value={newEntry.content}
                  onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                  placeholder="Enter the knowledge content..."
                  rows={4}
                  style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9', fontSize: '14px', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAddEntry(false)} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #334155', borderRadius: '8px', color: '#94a3b8', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" style={{ padding: '8px 16px', background: 'var(--brand-primary, #3b82f6)', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer' }}>
                  Create Entry
                </button>
              </div>
            </form>
          )}
          
          {loading ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: '32px' }}>Loading...</div>
          ) : entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <FileText size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
              <div style={{ color: '#94a3b8', fontSize: '16px' }}>No entries yet</div>
              <div style={{ color: '#64748b', fontSize: '13px' }}>Add entries manually or import from CSV</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {entries.map(entry => (
                <div key={entry.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ padding: '2px 8px', background: `${categoryColors[entry.category] || '#64748b'}20`, color: categoryColors[entry.category] || '#64748b', borderRadius: '4px', fontSize: '12px', textTransform: 'capitalize' }}>
                        {entry.category}
                      </span>
                      <span style={{ color: '#f1f5f9', fontWeight: 500 }}>{entry.title}</span>
                    </div>
                    <button 
                      onClick={() => handleDeleteEntry(entry.id)}
                      style={{ padding: '4px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '8px', lineHeight: 1.5 }}>
                    {truncateContent(entry.content)}
                  </div>
                  {entry.tags && entry.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                      {entry.tags.map(tag => (
                        <span key={tag} style={{ padding: '2px 6px', background: 'rgba(100, 116, 139, 0.2)', color: '#94a3b8', borderRadius: '4px', fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                          <Tag size={10} style={{ marginRight: '2px' }} />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                    <span style={{ color: '#64748b' }}>Created {formatDate(entry.created_at)}</span>
                    <span style={{ color: entry.enabled ? '#86efac' : '#fca5a5' }}>{entry.enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Query Tab */}
      {activeTab === 'query' && (
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: 600, margin: '0 0 20px 0' }}>Test Knowledge Base Query</h2>
          
          <form onSubmit={handleQuery} style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <input
                type="text"
                value={queryText}
                onChange={(e) => {
                  setQueryText(e.target.value);
                  if (!e.target.value.trim()) {
                    setQuerySubmitted(false);
                    setQueryResults([]);
                  }
                }}
                placeholder="Ask something about your inventory..."
                style={{ flex: 1, padding: '12px 16px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9', fontSize: '14px' }}
              />
              <button 
                type="submit" 
                disabled={queryLoading}
                style={{ padding: '12px 24px', background: 'var(--brand-primary, #3b82f6)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                {queryLoading ? (
                  <><RefreshCw size={16} style={{ marginRight: '6px', animation: 'spin 1s linear infinite' }} /> Searching...</>
                ) : (
                  <><Search size={16} style={{ marginRight: '6px' }} /> Search</>
                )}
              </button>
            </div>
          </form>
          
          {queryResults.length > 0 && (
            <div>
              <h3 style={{ color: '#f1f5f9', fontSize: '16px', margin: '0 0 16px 0' }}>
                {queryResults.length} result{queryResults.length !== 1 ? 's' : ''} for "{queryText}"
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {queryResults.map((result, index) => (
                  <div key={result.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <span style={{ padding: '2px 8px', background: '#334155', color: '#f1f5f9', borderRadius: '4px', fontSize: '12px' }}>#{index + 1}</span>
                      <span style={{ color: '#f1f5f9', fontWeight: 500, flex: 1 }}>{result.title}</span>
                      <span style={{ 
                        padding: '2px 8px', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        backgroundColor: result.confidence >= 0.8 ? 'rgba(34, 197, 94, 0.2)' : result.confidence >= 0.6 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: result.confidence >= 0.8 ? '#86efac' : result.confidence >= 0.6 ? '#fcd34d' : '#fca5a5'
                      }}>
                        {(result.confidence * 100).toFixed(0)}% match
                      </span>
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '8px', lineHeight: 1.5 }}>
                      {result.content}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ padding: '2px 8px', background: `${categoryColors[result.category] || '#64748b'}20`, color: categoryColors[result.category] || '#64748b', borderRadius: '4px', fontSize: '12px', textTransform: 'capitalize' }}>
                        {result.category}
                      </span>
                      {result.tags && result.tags.map(tag => (
                        <span key={tag} style={{ padding: '2px 6px', background: 'rgba(100, 116, 139, 0.2)', color: '#94a3b8', borderRadius: '4px', fontSize: '11px' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {querySubmitted && queryResults.length === 0 && !queryLoading && (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <Search size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
              <div style={{ color: '#94a3b8', fontSize: '16px' }}>No results found</div>
              <div style={{ color: '#64748b', fontSize: '13px' }}>Try a different query</div>
            </div>
          )}
        </div>
      )}
      
      {/* Tone & Style Tab */}
      {activeTab === 'tone' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: 600, margin: 0 }}>AI Tone & Style</h2>
            <button
              onClick={handleSaveTone}
              disabled={toneSaving}
              style={{ padding: '8px 16px', background: 'var(--brand-primary, #3b82f6)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              {toneSaving ? <><RefreshCw size={16} style={{ marginRight: '6px', animation: 'spin 1s linear infinite' }} /> Saving...</> : 'Save Settings'}
            </button>
          </div>
          
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ color: '#f1f5f9', fontSize: '16px', margin: '0 0 16px 0' }}>Tone Preset</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
              {TONE_PRESETS.map(preset => (
                <button
                  key={preset.value}
                  onClick={() => setTonePreset(preset.value)}
                  style={{
                    padding: '16px',
                    background: tonePreset === preset.value ? 'rgba(59, 130, 246, 0.2)' : '#0f172a',
                    border: `1px solid ${tonePreset === preset.value ? 'var(--brand-primary, #3b82f6)' : '#334155'}`,
                    borderRadius: '8px',
                    color: '#f1f5f9',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>{preset.label}</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>{preset.description}</div>
                </button>
              ))}
            </div>
            
            {tonePreset === 'custom' && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px', display: 'block' }}>Custom Tone Instructions</label>
                <textarea
                  value={toneCustom}
                  onChange={(e) => setToneCustom(e.target.value)}
                  placeholder="e.g., warm Aussie tone, concise, ask 1 question at a time"
                  rows={3}
                  style={{ width: '100%', padding: '10px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9', fontSize: '14px', resize: 'vertical' }}
                />
              </div>
            )}
          </div>
          
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px' }}>
            <h3 style={{ color: '#f1f5f9', fontSize: '16px', margin: '0 0 16px 0' }}>Conversion Assist</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={conversionAssistEnabled}
                  onChange={(e) => setConversionAssistEnabled(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ color: '#f1f5f9' }}>Enable conversion assistance</span>
              </label>
            </div>
            
            <div style={{ opacity: conversionAssistEnabled ? 1 : 0.5 }}>
              <label style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                Strength: {conversionAssistStrength} {conversionAssistStrength === 0 ? '(Minimal)' : conversionAssistStrength === 3 ? '(Aggressive)' : ''}
              </label>
              <input
                type="range"
                min="0"
                max="3"
                step="1"
                value={conversionAssistStrength}
                disabled={!conversionAssistEnabled}
                onChange={(e) => setConversionAssistStrength(Number(e.target.value))}
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                <span>Gentle clarify</span>
                <span>Tradeoffs + commitment</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Uploads Tab */}
      {activeTab === 'uploads' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: 600, margin: 0 }}>Uploaded Documents</h2>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile}
              style={{ padding: '8px 16px', background: uploadingFile ? '#334155' : '#10b981', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', cursor: uploadingFile ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}
            >
              {uploadingFile ? <><RefreshCw size={16} style={{ marginRight: '6px', animation: 'spin 1s linear infinite' }} /> Uploading...</> : <><Plus size={16} style={{ marginRight: '6px' }} /> Upload File</>}
            </button>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            accept=".md,.txt,.pdf,.csv,.json,text/markdown,text/plain,application/pdf,text/csv,application/json"
            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
          />
          
          {loading ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: '32px' }}>Loading...</div>
          ) : uploads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <FileText size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
              <div style={{ color: '#94a3b8', fontSize: '16px' }}>No uploads yet</div>
              <div style={{ color: '#64748b', fontSize: '13px' }}>Upload MD, PDF, TXT, CSV, or JSON files</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {uploads.map(upload => (
                <div key={upload.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '24px' }}>
                        {upload.file_type?.includes('pdf') ? '📄' : upload.file_type?.includes('markdown') || upload.filename?.endsWith('.md') ? '📝' : '📃'}
                      </span>
                      <div>
                        <div style={{ color: '#f1f5f9', fontWeight: 500 }}>{upload.filename}</div>
                        <div style={{ color: '#64748b', fontSize: '12px' }}>
                          {(upload.file_size / 1024).toFixed(1)} KB • Uploaded {formatDate(upload.created_at)}
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleDeleteUpload(upload.id)}
                      style={{ padding: '4px', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* AI Test Tab */}
      {activeTab === 'test' && (
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: 600, margin: '0 0 20px 0' }}>Test AI Responses</h2>
          
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <label style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '6px', display: 'block' }}>Test Prompt</label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={testPrompt}
                onChange={(e) => setTestPrompt(e.target.value)}
                placeholder="What are your opening hours?"
                style={{ flex: 1, padding: '12px 16px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: '#f1f5f9', fontSize: '14px' }}
              />
              <button
                onClick={runAiTest}
                disabled={testRunning || !testPrompt.trim()}
                style={{ padding: '12px 24px', background: 'var(--brand-primary, #3b82f6)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                {testRunning ? <><RefreshCw size={16} style={{ marginRight: '6px', animation: 'spin 1s linear infinite' }} /> Running...</> : <><Play size={16} style={{ marginRight: '6px' }} /> Test</>}
              </button>
            </div>
          </div>
          
          {testOutput && (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '20px' }}>
              <h3 style={{ color: '#f1f5f9', fontSize: '16px', margin: '0 0 12px 0' }}>AI Response</h3>
              <div style={{ 
                background: '#0f172a', 
                border: '1px solid #334155', 
                borderRadius: '8px', 
                padding: '16px',
                color: '#f1f5f9',
                fontSize: '14px',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap'
              }}>
                {testOutput}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KBV1Admin;
